import { Marquee } from "@/components/ui/marquee";
import { motion } from "framer-motion";
import { BrandLogo, type BrandName } from "@/components/icons/brand-logo";

type Integration = {
    name: string;
    brand: BrandName;
};

// SECURITY NOTE — this file intentionally OMITS internal infrastructure
// (AWS, Azure, MongoDB, Postgres, Redis, Docker, K8s, Sentry, PostHog,
// Ngrok, LiveKit, Steel, Remotion, Pinecone, Qdrant, LiteLLM, etc.).
// Marketing surfaces should advertise *what your workers can do*,
// not the substrate that runs them. Leaking the substrate narrows the
// attacker's search space (CVE targeting) and surrenders competitive
// signal (cost structure, vendor lock-in).
//
// What we DO show:
//   1. CONNECT_TO  — the apps a customer's digital workers operate inside
//   2. POWERED_BY  — recognizable foundation-model brands (trust signal)

const CONNECT_TO: Integration[] = [
    // Social & messaging
    { name: "Instagram", brand: "instagram" },
    { name: "Facebook", brand: "facebook" },
    { name: "LinkedIn", brand: "linkedin" },
    { name: "TikTok", brand: "tiktok" },
    { name: "YouTube", brand: "youtube" },
    { name: "X / Twitter", brand: "twitter" },
    { name: "Threads", brand: "threads" },
    { name: "WhatsApp", brand: "whatsapp" },
    { name: "Telegram", brand: "telegram" },
    { name: "Messenger", brand: "messenger" },
    { name: "Discord", brand: "discord" },
    // Email / outreach
    { name: "Gmail", brand: "gmail" },
    { name: "Outlook", brand: "outlook" },
    { name: "Apollo", brand: "apollo" },
    { name: "Mailchimp", brand: "mailchimp" },
    // Comms / collaboration
    { name: "Slack", brand: "slack" },
    { name: "Zoom", brand: "zoom" },
    { name: "Google Meet", brand: "googlemeet" },
    { name: "Twilio", brand: "twilio" },
    // CRM
    { name: "HubSpot", brand: "hubspot" },
    { name: "Salesforce", brand: "salesforce" },
    { name: "Twenty CRM", brand: "twenty" },
    { name: "Crunchbase", brand: "crunchbase" },
    // Productivity
    { name: "Notion", brand: "notion" },
    { name: "Google Sheets", brand: "googlesheets" },
    { name: "Google Drive", brand: "googledrive" },
    { name: "Airtable", brand: "airtable" },
    { name: "Asana", brand: "asana" },
    { name: "Trello", brand: "trello" },
    { name: "Jira", brand: "jira" },
    { name: "Dropbox", brand: "dropbox" },
    // Scheduling / forms
    { name: "Cal.com", brand: "cal" },
    { name: "Calendly", brand: "calendly" },
    { name: "Typeform", brand: "typeform" },
    // Commerce / payments
    { name: "Shopify", brand: "shopify" },
    { name: "Stripe", brand: "stripe" },
    { name: "PayPal", brand: "paypal" },
    { name: "QuickBooks", brand: "quickbooks" },
    { name: "Xero", brand: "xero" },
    // Dev
    { name: "GitHub", brand: "github" },
];

const POWERED_BY: Integration[] = [
    { name: "OpenAI", brand: "openai" },
    { name: "Anthropic", brand: "anthropic" },
    { name: "Google Gemini", brand: "gemini" },
    { name: "DeepSeek", brand: "deepseek" },
    { name: "Mistral", brand: "mistral" },
    { name: "xAI Grok", brand: "xai" },
    { name: "Moonshot Kimi", brand: "moonshot" },
    { name: "ElevenLabs", brand: "elevenlabs" },
    { name: "Hugging Face", brand: "huggingface" },
    { name: "Replicate", brand: "replicate" },
];

export function TrustBar() {
    return (
        <section
            id="trust-bar"
            aria-label="Platforms our digital workers operate on"
            className="py-16 px-6 md:px-12 lg:px-16 border-y border-border/50"
        >
            <div className="w-full max-w-7xl mx-auto space-y-12">
                {/* ROW 1 — Customer integrations */}
                <div>
                    <SectionLabel>
                        Your workers operate in {CONNECT_TO.length}+ tools you already use
                    </SectionLabel>
                    <LogoMarquee items={CONNECT_TO} duration={90} reverse={false} />
                </div>

                {/* ROW 2 — Foundation models / trust */}
                <div>
                    <SectionLabel>
                        Powered by leading AI from
                    </SectionLabel>
                    <LogoMarquee items={POWERED_BY} duration={50} reverse />
                </div>
            </div>
        </section>
    );
}

function SectionLabel({ children }: { children: React.ReactNode }) {
    return (
        <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="text-center font-body text-xs uppercase tracking-widest text-muted-foreground mb-8"
        >
            {children}
        </motion.p>
    );
}

function LogoMarquee({
    items,
    duration,
    reverse,
}: {
    items: Integration[];
    duration: number;
    reverse: boolean;
}) {
    return (
        <div className="relative">
            <Marquee
                durationSeconds={duration}
                pauseOnHover
                reverse={reverse}
            >
                {items.map((it) => (
                    <LogoTile key={it.name} {...it} />
                ))}
            </Marquee>
            {/* Edge fades */}
            <div className="pointer-events-none absolute inset-y-0 left-0 w-24 bg-gradient-to-r from-background to-transparent" />
            <div className="pointer-events-none absolute inset-y-0 right-0 w-24 bg-gradient-to-l from-background to-transparent" />
        </div>
    );
}

function LogoTile({ name, brand }: Integration) {
    return (
        <div
            className="flex items-center gap-3 mx-6 text-foreground/60 hover:text-foreground transition-colors duration-300"
            title={name}
        >
            <BrandLogo name={brand} className="w-6 h-6" />
            <span className="font-body text-sm whitespace-nowrap">{name}</span>
        </div>
    );
}
